/**
 * Utility functions for formatting numbers and currency
 */

export type CurrencyCode = 'USD' | 'EUR' | 'GBP' | 'JPY' | 'CAD' | 'AUD' | 'CHF' | 'CNY' | 'INR' | 'BRL'

/**
 * Format a number with commas (thousands separators)
 * @param value - The number to format
 * @returns Formatted string with commas (e.g., "1,234.56")
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    notation: 'compact',
    compactDisplay: 'short',
    minimumFractionDigits: 0,
    maximumFractionDigits: 20, // Allow up to 20 decimal places
  }).format(value)
}

/**
 * Format a number as currency with currency symbol and 2 decimal places
 * @param value - The number to format
 * @param currency - The currency code (default: 'GBP')
 * @param notation - The notation type (default: 'standard')
 * @returns Formatted currency string (e.g., "£1,234.56")
 */
export function formatCurrency(
  value: number,
  currency: CurrencyCode = 'GBP',
  notation: 'standard' | 'compact' = 'standard',
): string {
  const isWholeNumber = value % 1 === 0

  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency,
    notation: notation,
    compactDisplay: 'short',
    minimumFractionDigits: isWholeNumber ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Specifically format a price (Standard currency formatting, always GBP, always 2 decimals)
 * @param value - The price value to format
 * @returns Formatted price string (e.g., "£1,234.56")
 */
export function formatPrice(value: number | string): string {
  const numericValue = typeof value === 'string' ? Number.parseFloat(value) : value
  return formatCurrency(numericValue, 'GBP', 'standard')
}
